import React, { useEffect, useState } from 'react';
import { getFirestore, collection, onSnapshot, doc, updateDoc, query, where } from 'firebase/firestore';
import { onIdTokenChanged } from 'firebase/auth';
import { app, auth } from '../firebase';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';

const db = getFirestore(app);

export default function DevicesList() {
  const [devices, setDevices] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [userDomainId, setUserDomainId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setIsSystemAdmin(false);
        setUserDomainId(null);
        setAuthChecked(true);
        setLoading(false);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        setIsSystemAdmin(tokenResult?.claims?.role === 'SYSTEM_ADMIN');
        setUserDomainId(tokenResult?.claims?.domainId || null);
      } catch (e) {
        console.error(e);
      } finally {
        setAuthChecked(true);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authChecked) return;

    if (!isSystemAdmin && !userDomainId) {
      setLoading(false);
      return;
    }

    // Query participants for reference
    let qParticipants = query(collection(db, 'participants'));
    if (!isSystemAdmin && userDomainId) {
      qParticipants = query(collection(db, 'participants'), where('domainId', '==', userDomainId));
    }
    const unsubParticipants = onSnapshot(qParticipants, (snap) => {
      setParticipants(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // Query devices
    let qDevices = query(collection(db, 'devices'));
    if (!isSystemAdmin && userDomainId) {
      qDevices = query(collection(db, 'devices'), where('domainId', '==', userDomainId));
    }
    const unsubDevices = onSnapshot(qDevices, (snap) => {
      setDevices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('devices snapshot error', err);
      setLoading(false);
    });

    return () => {
      unsubParticipants();
      unsubDevices();
    };
  }, [authChecked, isSystemAdmin, userDomainId]);

  const handleInterfaceModeChange = async (deviceId, newMode) => {
    if (!newMode) return;
    setUpdatingId(deviceId);
    try {
      await updateDoc(doc(db, 'devices', deviceId), {
        interfaceMode: newMode
      });
    } catch (e) {
      console.error('Error updating interface mode', e);
      alert('Failed to update interface mode');
    } finally {
      setUpdatingId(null);
    }
  };

  const getParticipantName = (id) => {
    const p = participants.find(part => part.id === id);
    return p ? p.name : 'Unbonded';
  };

  if (loading) return <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Devices Configuration</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Manage the interface mode (VOICE or TOUCH) for each device. Changes are reflected immediately on the devices.
      </Typography>

      {devices.length === 0 ? (
        <Typography>No devices found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device ID</TableCell>
                <TableCell>Bonded To</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Interface Mode</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map(device => (
                <TableRow key={device.id} hover>
                  <TableCell title={device.id}>{device.id.substring(0, 8)}...</TableCell>
                  <TableCell>{getParticipantName(device.bondedToParticipantId)}</TableCell>
                  <TableCell>
                    {device.lastSeen?.toMillis ? new Date(device.lastSeen.toMillis()).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <ToggleButtonGroup
                      value={device.interfaceMode || 'VOICE'}
                      exclusive
                      onChange={(e, mode) => handleInterfaceModeChange(device.id, mode)}
                      size="small"
                      color="primary"
                      disabled={updatingId === device.id}
                    >
                      <ToggleButton value="VOICE">VOICE (Orb)</ToggleButton>
                      <ToggleButton value="TOUCH">TOUCH (Roster)</ToggleButton>
                    </ToggleButtonGroup>
                    {updatingId === device.id && <CircularProgress size={20} sx={{ ml: 1, verticalAlign: 'middle' }} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
